import { useState, useEffect } from 'react'
import { Button } from '@/components/common/Button'
import { AlertTriangle, Loader2, Trash2, Play, Clock } from 'lucide-react'
import { usePersistedBatch, PersistedBatchState, BULK_IMPORT_BATCHES_EVENT } from '@/hooks/usePersistedBatch'
import { bulkTimetableImportApi } from '@/services/api/bulkTimetableImport'
import { timetableImportV2Api } from '@/services/api/timetableImportV2'

interface BatchRecoveryBannerProps {
  onContinue: (batch: PersistedBatchState) => void | Promise<void>
}

function formatTimeElapsed(dateString: string): string {
  const elapsed = Date.now() - new Date(dateString).getTime()
  const minutes = Math.floor(elapsed / 60000)
  const hours = Math.floor(minutes / 60)
  
  if (hours > 0) {
    return `${hours}h ${minutes % 60}m`
  }
  if (minutes > 0) {
    return `${minutes}m`
  }
  return 'gerade'
}

export function BatchRecoveryBanner({ onContinue }: BatchRecoveryBannerProps) {
  const { getActiveBatches, syncWithBackend, removeBatch } = usePersistedBatch()
  const [batches, setBatches] = useState<PersistedBatchState[]>([])
  const [syncing, setSyncing] = useState(false)
  const [continuingId, setContinuingId] = useState<string | null>(null)
  const [timeElapsed, setTimeElapsed] = useState<Record<string, string>>({})

  // Update time elapsed every minute
  useEffect(() => {
    const updateTimes = () => {
      const newTimes: Record<string, string> = {}
      batches.forEach(b => {
        newTimes[b.batchId] = formatTimeElapsed(b.lastUpdated)
      })
      setTimeElapsed(newTimes)
    }
    updateTimes()
    const interval = setInterval(updateTimes, 60000)
    return () => clearInterval(interval)
  }, [batches])

  useEffect(() => {
    const load = async () => {
      setSyncing(true)
      const byId = new Map<string, PersistedBatchState>()
      try {
        const open = await timetableImportV2Api.listOpenBatches()
        for (const item of open.batches || []) {
          if (item.status === 'completed') continue
          byId.set(item.batch_id, {
            batchId: item.batch_id,
            jobId: null,
            fileId: item.uploaded_file_id,
            fileName: item.filename || 'Import',
            startedAt: item.created_at || new Date().toISOString(),
            lastUpdated: item.created_at || new Date().toISOString(),
            status: (item.status || 'extracting') as PersistedBatchState['status'],
            totalSections: item.total_sections || 0,
            resolvedSections: item.resolved_sections || 0,
            unresolvedSessionId: null,
          })
        }
      } catch {
        /* local list still shows started imports */
      }
      for (const b of getActiveBatches()) {
        const result = await syncWithBackend(b.batchId)
        if (!result) {
          byId.delete(b.batchId)
          continue
        }
        if (result.status !== 'completed') byId.set(result.batchId, { ...byId.get(result.batchId), ...result, jobId: result.jobId || byId.get(result.batchId)?.jobId || null })
        else byId.delete(result.batchId)
      }
      setBatches(Array.from(byId.values()))
      setSyncing(false)
    }
    load()
    window.addEventListener(BULK_IMPORT_BATCHES_EVENT, load)
    return () => window.removeEventListener(BULK_IMPORT_BATCHES_EVENT, load)
  }, [getActiveBatches, syncWithBackend])

  if (batches.length === 0 && !syncing) return null

  const statusLabels: Record<PersistedBatchState['status'], string> = {
    extracting: 'Extrahiert...',
    resolving: 'Token-Zuordnung nötig',
    awaiting_resolution: 'Token-Zuordnung nötig',
    awaiting_import: 'Bereit zum Import',
    importing: 'Importiert...',
    completed: 'Abgeschlossen',
    partially_failed: 'Teilweise fehlgeschlagen',
    pending_bulk_shop: 'Bulk-Zuordnung nötig',
    pending_assignment: 'Lehrer-Zuordnung nötig',
    failed: 'Unterbrochen',
    cancelled: 'Unterbrochen',
    incomplete: 'Unvollständig',
    pending: 'Gestartet',
  }

  const statusColors: Record<PersistedBatchState['status'], string> = {
    extracting: 'bg-blue-100 text-blue-800',
    resolving: 'bg-yellow-100 text-yellow-800',
    awaiting_resolution: 'bg-yellow-100 text-yellow-800',
    awaiting_import: 'bg-green-100 text-green-800',
    importing: 'bg-blue-100 text-blue-800',
    completed: 'bg-gray-100 text-gray-600',
    partially_failed: 'bg-red-100 text-red-800',
    pending_bulk_shop: 'bg-yellow-100 text-yellow-800',
    pending_assignment: 'bg-orange-100 text-orange-800',
    failed: 'bg-amber-100 text-amber-800',
    cancelled: 'bg-amber-100 text-amber-800',
    incomplete: 'bg-orange-100 text-orange-800',
    pending: 'bg-blue-100 text-blue-800',
  }

  const handleAbandon = async (batchId: string, fileName: string) => {
    if (!confirm(`Import für "${fileName}" wirklich abbrechen?`)) return
    try {
      await bulkTimetableImportApi.cleanupBatch(batchId, true)
    } catch {
      // Cleanup may fail if batch already removed - ignore
    }
    removeBatch(batchId)
    setBatches(prev => prev.filter(b => b.batchId !== batchId))
  }

  const handleCleanupOld = async () => {
    if (!confirm('Alle Batches die älter als 24 Stunden sind löschen?')) return
    try {
      const result = await bulkTimetableImportApi.cleanupOldBatches()
      alert(result.message)
      // Refresh batch list
      const stored = getActiveBatches()
      setBatches(stored)
    } catch (err: any) {
      alert('Fehler: ' + (err.response?.data?.detail || err.message))
    }
  }

  return (
    <div className="bg-orange-50 border border-orange-200 rounded-lg p-4 mb-4">
      <div className="flex items-center gap-2 mb-3">
        <AlertTriangle className="h-5 w-5 text-orange-600" />
        <span className="font-medium text-orange-800">
          {batches.length} unvollständige Import(s)
        </span>
        {syncing && <Loader2 className="h-4 w-4 animate-spin text-orange-600" />}
      </div>
      
      <div className="space-y-2">
        {batches.map(batch => (
          <div key={batch.batchId} className="flex items-center gap-3 bg-white rounded p-2 border">
            <div className="flex-1 min-w-0">
              <div className="font-medium text-sm truncate">{batch.fileName}</div>
              <div className="text-xs text-gray-500 flex items-center gap-2">
                <span>{batch.resolvedSections}/{batch.totalSections} Seiten</span>
                <span className="flex items-center gap-1 text-gray-400">
                  <Clock className="h-3 w-3" />
                  {timeElapsed[batch.batchId] || formatTimeElapsed(batch.lastUpdated)}
                </span>
              </div>
            </div>
            
            <span className={`text-xs px-2 py-1 rounded ${statusColors[batch.status]}`}>
              {statusLabels[batch.status]}
            </span>
            
            <div className="flex gap-1">
              <Button
                variant="primary"
                size="sm"
                disabled={continuingId === batch.batchId}
                onClick={async () => {
                  setContinuingId(batch.batchId)
                  try {
                    await onContinue(batch)
                  } finally {
                    setContinuingId(null)
                  }
                }}
                className="flex items-center gap-1"
              >
                {continuingId === batch.batchId ? (
                  <Loader2 className="h-3 w-3 animate-spin" />
                ) : (
                  <Play className="h-3 w-3" />
                )}
                Continue
              </Button>
              
              <Button
                variant="secondary"
                size="sm"
                onClick={() => handleAbandon(batch.batchId, batch.fileName)}
                className="flex items-center gap-1 text-red-600 border-red-300 hover:bg-red-50"
              >
                <Trash2 className="h-3 w-3" />
              </Button>
            </div>
          </div>
          ))}
      </div>

      <div className="mt-3 pt-3 border-t border-orange-200">
        <Button
          variant="secondary"
          size="sm"
          onClick={handleCleanupOld}
          className="flex items-center gap-1 text-red-600 border-red-300 hover:bg-red-50"
        >
          <Trash2 className="h-3 w-3" />
          Alte Batches löschen (&gt;24h)
        </Button>
      </div>
    </div>
  )
}